import {
  Close,
  SentimentSatisfied,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
} from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  GenButton,
  GenFileUpload,
} from '@pinnacleautomotive/clarity-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WOQuality } from '../../../common/enums/enums';
import { amplifyApi } from '../../../hooks/amplifyApi';

const Rating = ({ topic, value, onChange }) => {
  const { t } = useTranslation('orders');
  return (
    <Stack alignItems="center">
      <Typography variant="overline">{topic}</Typography>
      <Stack direction="row" spacing={1}>
        <Tooltip title={t('survey.modal.below')} arrow>
          <IconButton
            onClick={() => onChange(WOQuality.BELOW)}
            data-testid="belowFace"
          >
            <SentimentVeryDissatisfied
              fontSize="large"
              color={value === WOQuality.BELOW ? 'error' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('survey.modal.meets')} arrow>
          <IconButton
            onClick={() => onChange(WOQuality.MEETS)}
            data-testid="meetsFace"
          >
            <SentimentSatisfied
              fontSize="large"
              color={value === WOQuality.MEETS ? 'warning' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('survey.modal.above')} arrow>
          <IconButton
            onClick={() => onChange(WOQuality.ABOVE)}
            data-testid="aboveFace"
          >
            <SentimentVerySatisfied
              fontSize="large"
              color={value === WOQuality.ABOVE ? 'success' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
};

const SurveyModal = ({ open, data, onClose, onSubmit, isLoading }) => {
  const [workQuality, setWorkQuality] = useState(null);
  const [deliveryTime, setDeliveryTime] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [additionalComments, setAdditionalComments] = useState('');
  const { t } = useTranslation('orders');

  const submit = () => {
    onSubmit({
      workQuality,
      deliveryTime,
      additionalComments,
      images: images.map(image => image.fileId),
    });
    setImages([]);
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth={true} onClose={onClose}>
      <DialogTitle sx={{ background: theme => theme.palette.grey[50] }}>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography variant="subtitle2" flexGrow={1}>
            {t('survey.modal.title')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Stack divider={<Divider flexItem />} py={3.25} spacing={3}>
          <Stack justifyContent="center" alignItems="center">
            <Typography variant="h6">
              {t('tableHeaders.wo')} {data?.woNumber?.replace('WO-', '')}
            </Typography>
            <Typography variant="body1">{data?.dealer?.name}</Typography>
          </Stack>
          <Stack justifyContent="center" alignItems="center" spacing={2}>
            <Rating
              topic={t('survey.modal.quality')}
              value={workQuality}
              onChange={setWorkQuality}
            />
            <Rating
              topic={t('survey.modal.time')}
              value={deliveryTime}
              onChange={setDeliveryTime}
            />
            <Stack width="100%">
              <Typography variant="overline">
                {t('survey.modal.comments')}
              </Typography>
              <TextField
                variant="outlined"
                multiline
                rows={3}
                onChange={event => setAdditionalComments(event.target.value)}
                placeholder={t('survey.modal.commentsPlaceHolder')}
                inputProps={{ maxLength: 700 }}
              />
            </Stack>
            <Stack width="100%">
              <Typography variant="overline">
                {t('survey.modal.comments')}
              </Typography>
              <GenFileUpload
                variant="dense"
                accept={['image/jpeg']}
                multiple={true}
                onChangeLoading={isUploadingFiles =>
                  setIsUploading(isUploadingFiles)
                }
                uploadUrl={'/uploads/surveys/photos'}
                amplifyApi={amplifyApi}
                onChange={setImages}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GenButton
          text={t('global:genericButtons.cancel')}
          order={0}
          onClick={onClose}
          disabled={isLoading}
          sx={{
            color: theme => theme.palette.text.primary,
            borderColor: theme => theme.palette.divider,
          }}
        />
        <GenButton
          text={t('survey.modal.submit')}
          order={1}
          color="primary"
          variant="contained"
          loading={isLoading || isUploading}
          disabled={!workQuality || !deliveryTime}
          onClick={submit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default SurveyModal;
